import { dispatch, getState } from '..';
import { EmployeeHelper, IEmployee } from '../../@types/employee';
import DataActions from './actions';
import firebase from 'firebase/app';
import firebaseApp, { FirebaseService } from '../../services/firebaseService';
import lodash from 'lodash';
import generalFunctions from '../general/functions';
import DivisionHelper, { IDivision } from '../../@types/model/masterData/division';
import { TransformerGroupHelper } from '../../@types/model/transformer/transformerGroup';
import TransformerUsageHelper from '../../@types/model/transformer/transformerUsage';
import TransformerAttributeHelper from '../../@types/model/transformer/transformerAttribute';
import CompanyHelper from '../../@types/model/masterData/company';
import HttpGeoServerService from '../../services/http/geoserverHttpService';
import { Area } from '../../@types/model/area';

export default class DataFunctions {
    private static EMPLOYEE_COLLECTION = 'employee';
    private static BOREHOLE_GROUNDWATERZONES = 'boreholeGroundwaterZones';

    private static divisionListener ?: () => void;
    private static groundwaterListener ?: () => void;
    private static transformerGroupListener ?: () => void;
    private static transformerUsageListener ?: () => void;
    private static transformerAttributesListener ?: () => void;
    private static companyListener ?: () => void;

    public static readonly setDivisionsLoading = (loading : boolean) => {
        dispatch(DataActions.dataSetLoadingDivisions(loading));
    };

    public static readonly getDivisions = async () => {
        if (DataFunctions.divisionListener) return;

        DataFunctions.setDivisionsLoading(true);
        dispatch(DataActions.dataSetDivisions([]));

        try {
            DataFunctions.divisionListener = DivisionHelper
                .listen()
                .onSnapshot((snapshot) => {
                    const dataState = getState().data;

                    const divisions = dataState.divisions.slice();

                    // "added" | "removed" | "modified"
                    snapshot.docChanges().forEach((f) => {
                        const division = f.doc.data();

                        switch (f.type) {
                            case 'added':
                                divisions.splice(f.newIndex, 0, division);
                                break;
                            case 'modified':
                                divisions.splice(f.newIndex, 1, division);
                                break;
                            case 'removed':
                                divisions.splice(f.newIndex, 1);
                                break;
                        }
                    });

                    dispatch(DataActions.dataSetDivisions(divisions));
                    DataFunctions.setDivisionsLoading(false);
                });

        } catch (ex) {
            generalFunctions.generalShowErrorSnackbar('An error while loading divisions.');
            DataFunctions.setDivisionsLoading(false);
        }
    };

    public static readonly saveDivision = async (division : IDivision) => {

        const session = getState().auth.session;

        if (!session) return;

        DataFunctions.setDivisionsLoading(true);

        try {
            const save : IDivision = {
                ...division,

                UpdatedOn: firebase.firestore.Timestamp.now(),
                UpdatedBy: firebaseApp.firestore().collection(DataFunctions.EMPLOYEE_COLLECTION).doc(session.firebaseUser.uid),
                UpdatedByEmployee: session.firebaseUser.displayName ? session.firebaseUser.displayName : '',
                UpdatedByName: session.firebaseUser.displayName ? session.firebaseUser.displayName : '',
                IsActive: !!division.IsActive,
            };

            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (!division.CreatedBy) {
                division.CreatedOn = firebase.firestore.Timestamp.now();
                division.CreatedBy = firebaseApp.firestore().collection(DataFunctions.EMPLOYEE_COLLECTION).doc(session.firebaseUser.uid);
                division.CreatedByName = session.firebaseUser.displayName ? session.firebaseUser.displayName : '';
            }

            await DivisionHelper
                .save(save);

        } catch (ex) {
            generalFunctions.generalShowErrorSnackbar(`An error while saving Division. ${ex}`);
        } finally {
            DataFunctions.setDivisionsLoading(false);
        }
    };

    public static readonly getAreas = async (refresh ?: boolean) => {

        const dataState = getState().data;

        if (dataState.isLoadingAreas) return;
        if (dataState.areas.length && !refresh) return;

        try {
            dispatch(DataActions.setLoadingAreas(true));

            const result = await HttpGeoServerService.getDepartments();

            const areas = result.data.features.map(n => new Area(n.properties));
            dispatch(DataActions.setAreas(areas));
        } catch (ex) {
            if (ex) {
                generalFunctions.generalShowErrorSnackbar('An error while loading areas.');
            }
        } finally {
            dispatch(DataActions.setLoadingAreas(false));
        }
    };

    public static readonly getDepartments = async (refresh ?: boolean) => {

        const dataState = getState().data;

        if (dataState.isLoadingDepartments) return;
        if (dataState.departments.length && !refresh) return;

        try {
            dispatch(DataActions.setLoadingDepartments(true));

            const result = await FirebaseService.getDepartments();

            dispatch(DataActions.setDepartments(result));
        } catch (ex) {
            if (ex) {
                generalFunctions.generalShowErrorSnackbar('An error while loading departments.');
            }
        } finally {
            dispatch(DataActions.setLoadingDepartments(false));
        }
    };

    public static readonly getTransformerGroups = async (refresh ?: boolean) => {

        const dataState = getState().data;

        if (dataState.isLoadingTransformerGroups) return;
        if (dataState.transformerGroups.length && !refresh) return;

        if (DataFunctions.transformerGroupListener) DataFunctions.transformerGroupListener();

        try {
            dispatch(DataActions.setLoadingTransformerGroups(true));
            DataFunctions.transformerGroupListener = TransformerGroupHelper.listen().onSnapshot((snapshot) => {
                const groups = getState().data.transformerGroups.slice();

                snapshot.docChanges().forEach((f) => {
                    const group = f.doc.data();
                    const index = lodash.findIndex(groups, n => n.id === f.doc.id);

                    switch (f.type) {
                        case 'added':
                            groups.push(group);
                            break;
                        case 'modified':
                            groups.splice(index, 1, group);
                            break;
                        case 'removed':
                            groups.splice(index, 1);
                            break;
                    }
                });

                dispatch(DataActions.setTransformerGroups(lodash.sortBy(groups, p => p.name)));
                dispatch(DataActions.setLoadingTransformerGroups(false));
            }, (error) => {
                generalFunctions.generalShowErrorSnackbar(error.message ? error.message : 'Error loading groups.');
                dispatch(DataActions.setLoadingTransformerGroups(false));
            });
        } catch (ex) {
            if (ex) {
                generalFunctions.generalShowErrorSnackbar('An error while loading groups.');
            }
            dispatch(DataActions.setLoadingTransformerGroups(false));
        }
    };
    
    public static readonly getTransformerUsages = async (refresh ?: boolean) => {

        const dataState = getState().data;

        if (dataState.isLoadingTransformerUsages) return;
        if (dataState.transformerUsages.length && !refresh) return;

        if (DataFunctions.transformerUsageListener) DataFunctions.transformerUsageListener();

        try {
            dispatch(DataActions.setLoadingTransformerUsages(true));
            DataFunctions.transformerUsageListener = TransformerUsageHelper.listen().onSnapshot((snapshot) => {
                const usages = getState().data.transformerUsages.slice();

                snapshot.docChanges().forEach((f) => {
                    const usage = f.doc.data();
                    const index = lodash.findIndex(usages, n => n.id === f.doc.id);

                    switch (f.type) {
                        case 'added':
                            usages.push(usage);
                            break;
                        case 'modified':
                            usages.splice(index, 1, usage);
                            break;
                        case 'removed':
                            usages.splice(index, 1);
                            break;
                    }
                });

                dispatch(DataActions.setTransformerUsages(lodash.sortBy(usages, p => parseInt(p.code.substr(2)))));
                dispatch(DataActions.setLoadingTransformerUsages(false));
            }, (error) => {
                generalFunctions.generalShowErrorSnackbar(error.message ? error.message : 'Error loading usages.');
                dispatch(DataActions.setLoadingTransformerUsages(false));
            });
        } catch (ex) {
            if (ex) {
                generalFunctions.generalShowErrorSnackbar('An error while loading usages.');
            }
            dispatch(DataActions.setLoadingTransformerUsages(false));
        }
    };

    public static readonly getTransformerAttributes = async (refresh ?: boolean) => {

        const dataState = getState().data;

        if (dataState.isLoadingTransformerAttributes) return;
        if (dataState.transformerAttributes.length && !refresh) return;

        if (DataFunctions.transformerAttributesListener) DataFunctions.transformerAttributesListener();

        try {
            dispatch(DataActions.setLoadingTransformerAttributes(true));
            DataFunctions.transformerAttributesListener = TransformerAttributeHelper.listen().onSnapshot((snapshot) => {
                const attributes = getState().data.transformerAttributes.slice();

                snapshot.docChanges().forEach((f) => {
                    const attribute = f.doc.data();
                    const index = lodash.findIndex(attributes, n => n.Id === f.doc.id);

                    switch (f.type) {
                        case 'added':
                            attributes.push(attribute);
                            break;
                        case 'modified':
                            attributes.splice(index, 1, attribute);
                            break;
                        case 'removed':
                            attributes.splice(index, 1);
                            break;
                    }
                });

                dispatch(DataActions.setTransformerAttributes(attributes));
                dispatch(DataActions.setLoadingTransformerAttributes(false));
            }, (error) => {
                generalFunctions.generalShowErrorSnackbar(error.message ? error.message : 'Error loading attributes.');
                dispatch(DataActions.setLoadingTransformerAttributes(false));
            });
        } catch (ex) {
            if (ex) {
                generalFunctions.generalShowErrorSnackbar('An error while loading attributes.');
            }
            dispatch(DataActions.setLoadingTransformerAttributes(false));
        }
    };

    public static readonly getCompanies = async (refresh ?: boolean) => {

        const dataState = getState().data;

        if (dataState.isLoadingCompanies) return;
        if (dataState.companies.length && !refresh) return;

        if (DataFunctions.companyListener) DataFunctions.companyListener();

        try {
            dispatch(DataActions.setLoadingCompany(true));
            DataFunctions.companyListener = CompanyHelper.listen().onSnapshot((snapshot) => {
                const items = getState().data.companies.slice();

                snapshot.docChanges().forEach((f) => {
                    const item = f.doc.data();
                    const index = lodash.findIndex(items, n => n.id === f.doc.id);

                    switch (f.type) {
                        case 'added':
                            items.push(item);
                            break;
                        case 'modified':
                            items.splice(index, 1, item);
                            break;
                        case 'removed':
                            items.splice(index, 1);
                            break;
                    }
                });

                dispatch(DataActions.setCompanies(items));
                dispatch(DataActions.setLoadingCompany(false));
            }, (error) => {
                generalFunctions.generalShowErrorSnackbar(error.message ? error.message : 'Error loading companies.');
                dispatch(DataActions.setLoadingCompany(false));
            });
        } catch (ex) {
            if (ex) {
                generalFunctions.generalShowErrorSnackbar('An error while loading companies.');
            }
            dispatch(DataActions.setLoadingCompany(false));
        }
    };
}
